import { type CSSProperties, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ToasterProps } from "sonner";
import { Toaster as SonnerToaster, useSonner } from "sonner";
import { cx } from "@/utils/cx";
import { TOAST_WIDTH_PX, TOAST_Z_INDEX } from "@/utils/toast.utils";

export const DEFAULT_TOAST_POSITION = "top-right";

const toasterStyle = {
	"--width": `${TOAST_WIDTH_PX}px`,
	zIndex: TOAST_Z_INDEX,
} as CSSProperties;

/**
 * Global Sonner host. Portaled to `document.body` so toasts stack above
 * React Aria drawers/modals (`z-50` / `z-60`) instead of being trapped in Shell.
 */
export function AppToaster() {
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	if (!isMounted) {
		return null;
	}

	return createPortal(
		<SonnerToaster
			visibleToasts={9}
			position={DEFAULT_TOAST_POSITION}
			style={toasterStyle}
			toastOptions={{
				duration: 5000,
			}}
		/>,
		document.body,
	);
}

export const ToastsOverlay = () => {
	const { toasts } = useSonner();

	const styles = {
		"top-right": {
			className: "top-0 right-0",
			background:
				"linear-gradient(215deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.00) 50%)",
		},
		"top-left": {
			className: "top-0 left-0",
			background:
				"linear-gradient(139deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.00) 40.64%)",
		},
		"bottom-right": {
			className: "bottom-0 right-0",
			background:
				"linear-gradient(148deg, rgba(0, 0, 0, 0.00) 58.58%, rgba(0, 0, 0, 0.10) 97.86%)",
		},
		"bottom-left": {
			className: "bottom-0 left-0",
			background:
				"linear-gradient(214deg, rgba(0, 0, 0, 0.00) 54.54%, rgba(0, 0, 0, 0.10) 95.71%)",
		},
	};

	// Deduplicated list of positions
	const positions = toasts.reduce<NonNullable<ToasterProps["position"]>[]>(
		(acc, t) => {
			acc.push(t.position || DEFAULT_TOAST_POSITION);
			return acc;
		},
		[],
	);

	return (
		<>
			{Object.entries(styles).map(([position, style]) => (
				<div
					key={position}
					className={cx(
						"pointer-events-none fixed hidden h-72.5 w-130 transition duration-500 xs:block",
						style.className,
						positions.includes(position as keyof typeof styles)
							? "visible opacity-100"
							: "invisible opacity-0",
					)}
					style={{
						background: style.background,
						zIndex: TOAST_Z_INDEX,
					}}
				/>
			))}
			<div
				className={cx(
					"pointer-events-none fixed right-0 bottom-0 left-0 h-67.5 w-full bg-linear-to-t from-black/10 to-transparent transition duration-500 xs:hidden",
					positions.length > 0 ? "visible opacity-100" : "invisible opacity-0",
				)}
				style={{ zIndex: TOAST_Z_INDEX }}
			/>
		</>
	);
};

/** @deprecated Prefer `AppToaster` — kept for Storybook / DS demos. */
export const Toaster = () => (
	<>
		<SonnerToaster position={DEFAULT_TOAST_POSITION} style={toasterStyle} />
		<ToastsOverlay />
	</>
);
